import i18n from "../i18n";

/** The appraisal from the employee's side.
 *
 * reviewStatus.ts describes an appraisal for HR and managers ("The employee
 * is writing their self-assessment"). These helpers say the same things to
 * the person being appraised, in the second person, and hold the rating scale
 * so every rating control and every rating shown uses the same words.
 */

const t = (key: string, fallback: string, opts?: Record<string, unknown>) =>
  i18n.t(`appraisal.${key}`, { defaultValue: fallback, ...(opts ?? {}) });

/* ── Rating scale ────────────────────────────────────────────────────────── */

export interface RatingLevel {
  value: number;
  label: string;
  hint: string;
}

/** The company 1–5 scale. The two ends keep the wording the system already
 *  used (1 Needs improvement, 5 Outstanding). */
export function ratingScale(): RatingLevel[] {
  return [
    { value: 1, label: t("rating.1", "Needs improvement"), hint: t("rating.1hint", "Falls short of what the role needs. Support is needed.") },
    { value: 2, label: t("rating.2", "Developing"), hint: t("rating.2hint", "Meets some of what the role needs and is still building.") },
    { value: 3, label: t("rating.3", "Meets expectations"), hint: t("rating.3hint", "A solid job. Consistently delivers what the role needs. Most good performers sit here.") },
    { value: 4, label: t("rating.4", "Exceeds expectations"), hint: t("rating.4hint", "Goes above what the role needs. Needs an example of how.") },
    { value: 5, label: t("rating.5", "Outstanding"), hint: t("rating.5hint", "Exceptional. Sets the standard for others. Needs clear examples.") },
  ];
}

/* ── Manager ratings: a 4 or 5 needs an example ───────────────────────────── */

/** The same rule the server applies before a manager review can be submitted. */
export const EXAMPLE_REQUIRED_FROM = 4;
export const MIN_EXAMPLE_CHARS = 30;

export function needsExample(rating: number | null | undefined): boolean {
  return rating != null && rating >= EXAMPLE_REQUIRED_FROM;
}

export function hasExample(text: string | null | undefined): boolean {
  return (text ?? "").trim().length >= MIN_EXAMPLE_CHARS;
}

/** A rating of 4 or 5 without a real example. */
export function missingExample(rating: number | null | undefined, text: string | null | undefined): boolean {
  return needsExample(rating) && !hasExample(text);
}

/* ── Self and colleague ratings: words, not the company scale ─────────────── */

/** How someone sees their own work (or a colleague's). Four words, stored as
 *  1–4, so it reads as a reflection and is never mistaken for the company
 *  1–5 rating the manager gives. */
export function reflectionScale(perspective: "self" | "colleague" = "self"): RatingLevel[] {
  const self = perspective === "self";
  return [
    {
      value: 1, label: t("reflect.1", "Needs support"),
      hint: self ? t("reflect.1self", "I'm finding this hard and would like more help.") : t("reflect.1peer", "They'd benefit from more help here."),
    },
    {
      value: 2, label: t("reflect.2", "Getting there"),
      hint: self ? t("reflect.2self", "Making progress, not yet consistent.") : t("reflect.2peer", "Making progress, not yet consistent."),
    },
    {
      value: 3, label: t("reflect.3", "Going well"),
      hint: self ? t("reflect.3self", "I do this well most of the time.") : t("reflect.3peer", "They do this well most of the time."),
    },
    {
      value: 4, label: t("reflect.4", "A real strength"),
      hint: self ? t("reflect.4self", "One of my strongest areas.") : t("reflect.4peer", "One of their strongest areas."),
    },
  ];
}

/** A self or colleague rating as shown anywhere: the word when the appraisal
 *  uses words, otherwise the old 1–5 number with its label. */
export function selfRatingText(value: number | null | undefined, words: boolean | null | undefined): string {
  if (value == null) return "–";
  if (words) return reflectionScale().find((l) => l.value === value)?.label ?? String(value);
  return ratingText(value);
}

/* ── The name ────────────────────────────────────────────────────────────── */

/** "2026 appraisal" (or "Appraisal" when the year is unknown). */
export function appraisalName(year: number | null | undefined, capital = true): string {
  if (year) return t("name", "{{year}} appraisal", { year });
  return capital ? t("nameNoYear", "Appraisal") : t("nameNoYearLower", "appraisal");
}

/** "3 · Meets expectations", or "–" when there is no rating. */
export function ratingText(value: number | null | undefined): string {
  if (value == null) return "–";
  const level = ratingScale().find((l) => l.value === value);
  return level ? `${value} · ${level.label}` : String(value);
}

export function ratingLabel(value: number | null | undefined): string {
  if (value == null) return "";
  return ratingScale().find((l) => l.value === value)?.label ?? "";
}

/* ── Dates ───────────────────────────────────────────────────────────────── */

function locale(): string {
  const lang = i18n.language || "en-GB";
  return lang.startsWith("es") ? "es-ES" : lang === "en-US" ? "en-US" : "en-GB";
}

/** "4 Nov 2026" (dates from the API are plain YYYY-MM-DD). */
export function formatDate(iso: string | null | undefined, style: "short" | "long" = "short"): string {
  if (!iso) return "";
  const d = iso.length === 10 ? new Date(`${iso}T12:00:00`) : new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat(locale(), {
    day: "numeric",
    month: style === "long" ? "long" : "short",
    year: "numeric",
  }).format(d);
}

/** Whole days from today to a YYYY-MM-DD date (negative when past). */
export function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const target = new Date(`${iso.slice(0, 10)}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / 86400000);
}

/** "Due in 5 days", "Due today", "3 days overdue". */
export function dueText(iso: string | null | undefined): string {
  const days = daysUntil(iso);
  if (days == null) return "";
  if (days < 0) return t("overdueBy", "{{count}} days overdue", { count: Math.abs(days) });
  if (days === 0) return t("dueToday", "Due today");
  if (days === 1) return t("dueTomorrow", "Due tomorrow");
  return t("dueIn", "Due in {{count}} days", { count: days });
}

/* ── The journey ─────────────────────────────────────────────────────────── */

export type AppraisalStageKey = "self" | "peers" | "manager" | "meeting" | "done";

export interface AppraisalStage {
  key: AppraisalStageKey;
  title: string;
  who: string;
  detail: string;
}

/** The five stages of an appraisal, as the employee sees them. */
export function appraisalStages(managerName?: string | null): AppraisalStage[] {
  const manager = managerName || t("yourManager", "your manager");
  return [
    {
      key: "self",
      title: t("stage.self", "Your self-assessment"),
      who: t("who.you", "You"),
      detail: t("stage.selfDetail", "Look back on your year in 9 short steps. It saves as you go."),
    },
    {
      key: "peers",
      title: t("stage.peers", "Colleague feedback"),
      who: t("who.colleagues", "A few colleagues"),
      detail: t("stage.peersDetail", "Your manager may ask a few colleagues for feedback. You see themes, never who said what."),
    },
    {
      key: "manager",
      title: t("stage.manager", "Manager's review"),
      who: manager,
      detail: t("stage.managerDetail", "{{manager}} writes their view of your year.", { manager }),
    },
    {
      key: "meeting",
      title: t("stage.meeting", "Review meeting"),
      who: t("who.both", "You and {{manager}}", { manager }),
      detail: t("stage.meetingDetail", "You talk it through and agree a final rating and your objectives."),
    },
    {
      key: "done",
      title: t("stage.done", "Outcome"),
      who: t("who.you", "You"),
      detail: t("stage.doneDetail", "Read the outcome and add your own response."),
    },
  ];
}

const STAGE_OF: Record<string, AppraisalStageKey> = {
  not_started: "self",
  self_assessment_in_progress: "self",
  self_assessment_submitted: "peers",
  colleague_feedback_in_progress: "peers",
  colleague_feedback_complete: "manager",
  manager_review_in_progress: "manager",
  manager_review_submitted: "meeting",
  meeting_pending: "meeting",
  employee_response_pending: "done",
  completed: "done",
};

export function appraisalStageIndex(status: string): number {
  const key = STAGE_OF[status];
  if (!key) return -1;
  return ["self", "peers", "manager", "meeting", "done"].indexOf(key);
}

/** Is the next move the employee's? */
export function isYourMove(status: string): boolean {
  return ["not_started", "self_assessment_in_progress", "employee_response_pending"].includes(status);
}

/** What is happening now, said to the employee. */
export function appraisalNow(status: string, managerName?: string | null): { title: string; body: string } {
  const manager = managerName || t("yourManager", "your manager");
  switch (status) {
    case "not_started":
      return {
        title: t("now.notStartedTitle", "Your appraisal is open"),
        body: t("now.notStarted", "Start with your self-assessment. It takes about 30 to 45 minutes and saves as you go, so you can stop and come back."),
      };
    case "self_assessment_in_progress":
      return {
        title: t("now.inProgressTitle", "You've started your self-assessment"),
        body: t("now.inProgress", "Pick up where you left off. Everything you've written so far is saved."),
      };
    case "self_assessment_submitted":
    case "colleague_feedback_in_progress":
      return {
        title: t("now.peersTitle", "Thanks, your self-assessment is in"),
        body: t("now.peers", "{{manager}} may now ask a few colleagues for feedback. There's nothing for you to do at the moment.", { manager: cap(manager) }),
      };
    case "colleague_feedback_complete":
    case "manager_review_in_progress":
      return {
        title: t("now.managerTitle", "{{manager}} is writing their review", { manager: cap(manager) }),
        body: t("now.manager", "There's nothing for you to do at the moment. You'll hear when it's time for your review meeting."),
      };
    case "manager_review_submitted":
    case "meeting_pending":
      return {
        title: t("now.meetingTitle", "Time for your review meeting"),
        body: t("now.meeting", "{{manager}} will arrange a meeting to talk it through with you. Have a think about anything you'd like to raise.", { manager: cap(manager) }),
      };
    case "employee_response_pending":
      return {
        title: t("now.responseTitle", "Your appraisal is complete"),
        body: t("now.response", "Read the outcome and add your response when you're ready."),
      };
    case "completed":
      return {
        title: t("now.completedTitle", "Your appraisal is complete"),
        body: t("now.completed", "You can read the outcome and add or change your response at any time."),
      };
    default:
      return { title: "", body: "" };
  }
}

function cap(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/* ── Self-assessment steps ───────────────────────────────────────────────── */

export interface SelfStep {
  n: number;
  short: string;
  title: string;
  intro: string;
  tips: string[];
}

export function selfAssessmentSteps(): SelfStep[] {
  return [
    {
      n: 1, short: t("step.1short", "Responsibilities"), title: t("step.1", "Your key responsibilities"),
      intro: t("step.1intro", "List the main parts of your job (your job description is a good guide), say how each went this year and rate yourself."),
      tips: [t("step.1tip1", "Give a real example for each one."), t("step.1tip2", "Be honest: this is the start of a conversation, not a test.")],
    },
    {
      n: 2, short: t("step.2short", "Strengths"), title: t("step.2", "Your strengths"),
      intro: t("step.2intro", "What do you do well? Add an example from this year for each strength."),
      tips: [t("step.2tip1", "Think about what colleagues come to you for.")],
    },
    {
      n: 3, short: t("step.3short", "Development"), title: t("step.3", "Areas to develop"),
      intro: t("step.3intro", "Where would you like to get better, and what progress have you made so far?"),
      tips: [t("step.3tip1", "Include anything you'd like training or support with.")],
    },
    {
      n: 4, short: t("step.4short", "Do better"), title: t("step.4", "What could you do better?"),
      intro: t("step.4intro", "An honest look at what you would do differently next time."),
      tips: [],
    },
    {
      n: 5, short: t("step.5short", "Behaviours"), title: t("step.5", "How you work"),
      intro: t("step.5intro", "For each of PME's behaviours, give an example of how you showed it and rate yourself."),
      tips: [t("step.5tip1", "One short example is enough. Leave a behaviour blank if you have no example.")],
    },
    {
      n: 6, short: t("step.6short", "Objectives"), title: t("step.6", "Objectives for next year"),
      intro: t("step.6intro", "Suggest objectives for the year ahead. You'll agree the final ones with your manager in your review meeting; they then sit at the top of your Performance page and you go through them at every catch-up."),
      tips: [t("step.6tip1", "Good objectives say what will be done and by when.")],
    },
    {
      n: 7, short: t("step.7short", "Comments"), title: t("step.7", "Anything else"),
      intro: t("step.7intro", "Anything else you'd like your manager to know before your review."),
      tips: [],
    },
    {
      n: 8, short: t("step.8short", "Aspirations"), title: t("step.8", "Your role and aspirations"),
      intro: t("step.8intro", "Where would you like your role to go? What would you like to be doing in a year or two?"),
      tips: [],
    },
    {
      n: 9, short: t("step.9short", "Overall"), title: t("step.9", "Your overall rating"),
      intro: t("step.9intro", "Rate your year overall and name your top two priorities. Then submit your self-assessment to your manager."),
      tips: [],
    },
  ];
}
